import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route as R, Routes } from 'react-router-dom';
import { Route } from './enums';
import { AppShell } from './components/shared/AppShell';
import { RequireAuth } from './components/shared/RequireAuth';
import { RequireRole } from './components/shared/RequireRole';
import { Role } from '@mcper/core';
import { LoginPage } from './components/pages/Login/LoginPage';
import { AuthCallbackPage } from './components/pages/AuthCallback/AuthCallbackPage';

/* Pages are lazy-loaded so the first paint only ships the shell and the login screen. */
const AppsPage = lazy(() => import('./components/pages/Apps/AppsPage').then((m) => ({ default: m.AppsPage })));
const AttachAppPage = lazy(() => import('./components/pages/AttachApp/AttachAppPage').then((m) => ({ default: m.AttachAppPage })));
const AppDetailPage = lazy(() => import('./components/pages/AppDetail/AppDetailPage').then((m) => ({ default: m.AppDetailPage })));
const TestsPage = lazy(() => import('./components/pages/Tests/TestsPage').then((m) => ({ default: m.TestsPage })));
const TestsRunsPage = lazy(() => import('./components/pages/Tests/TestsRunsPage').then((m) => ({ default: m.TestsRunsPage })));
const TestAppPage = lazy(() => import('./components/pages/Tests/TestAppPage').then((m) => ({ default: m.TestAppPage })));
const OrgPage = lazy(() => import('./components/pages/Org/OrgPage').then((m) => ({ default: m.OrgPage })));
const AuditPage = lazy(() => import('./components/pages/Audit/AuditPage').then((m) => ({ default: m.AuditPage })));
const ConnectorsPage = lazy(() => import('./components/pages/Connectors/ConnectorsPage').then((m) => ({ default: m.ConnectorsPage })));
const ConnectorEditorPage = lazy(() => import('./components/pages/Connectors/ConnectorEditorPage').then((m) => ({ default: m.ConnectorEditorPage })));
const MyAgentsPage = lazy(() => import('./components/pages/MyAgents/MyAgentsPage').then((m) => ({ default: m.MyAgentsPage })));
const ConnectGuidePage = lazy(() => import('./components/pages/Connect/ConnectGuidePage').then((m) => ({ default: m.ConnectGuidePage })));
const AuthorizePage = lazy(() => import('./components/pages/Authorize/AuthorizePage').then((m) => ({ default: m.AuthorizePage })));

/** History router (clean URLs; nginx and Vite fall back to index.html). The server's /auth/callback redirects to `${PORTAL_URL}/signed-in`, which exchanges the refresh cookie for a token. */
export function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={null}>
      <Routes>
        <R path={Route.Login} element={<LoginPage />} />
        <R path={Route.AuthCallback} element={<AuthCallbackPage />} />
        {/* Anyone with the link: no sign-in, no shell. */}
        <R path={Route.ConnectorGuide} element={<ConnectGuidePage />} />
        <R element={<RequireAuth />}>
          {/* The page an agent opens: signed in, but outside the portal shell. */}
          <R path={Route.Authorize} element={<AuthorizePage />} />
          <R element={<AppShell />}>
            <R path={Route.Apps} element={<AppsPage />} />
            <R path={Route.AttachApp} element={<AttachAppPage />} />
            <R path={Route.EditApp} element={<AttachAppPage />} />
            <R path={Route.AppDetail} element={<AppDetailPage />} />
            <R path={Route.Connectors} element={<ConnectorsPage />} />
            <R path={Route.ConnectorNew} element={<ConnectorEditorPage />} />
            <R path={Route.ConnectorEdit} element={<ConnectorEditorPage />} />
            <R path={Route.Tests} element={<TestsPage />} />
            <R path={Route.TestsRuns} element={<TestsRunsPage />} />
            <R path={Route.TestApp} element={<TestAppPage />} />
            <R path={Route.Audit} element={<AuditPage />} />
            {/* Only the admin set on the server changes the workspace settings. */}
            <R element={<RequireRole minimum={Role.Admin} fallback={Route.Apps} />}>
              <R path={Route.Settings} element={<OrgPage />} />
            </R>
            <R path={Route.Org} element={<Navigate to={Route.Settings} replace />} />
            <R path={Route.MyAgents} element={<MyAgentsPage />} />
            <R path={Route.Connect} element={<Navigate to={Route.AttachApp} replace />} />
          </R>
        </R>
        <R path="*" element={<Navigate to={Route.Apps} replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
